/* eslint-disable max-lines-per-function */
import React, { useEffect, useState } from "react";
import { Routes, Route } from "react-router-dom";
import { LazyQueryHookOptions, useLazyQuery, useQuery } from "@apollo/client";

import "./interests-table.css";
import InterestProfileSlideout from "pages/dashboard/interests-profile-slideout/components/slideout";
import { Viewer } from "components/auth/types";
import { ApplicantSearch, ApplicantSearchVariables, getViewer } from "types/gql-types";
import { VIEWER_QUERY } from "components/auth/query";
import { formatFilters, useSidebarFilters } from "../sidebar-filters";
import { APPLICANT_SEARCH_QUERY } from "../interests-table/query";
import Pagination from "./pagination";
import Row from "./row";
import { useCanAssignOwner } from "./can-assign-owner";
import SelectionPrompt from "./selection-prompt";
import { TableResult, useInterestsTable } from "./table-context";

type StarMapType = Record<string, boolean>
type Props = {
  defaultSorting?: string;
  interestListId: string;
  slideoutEnabled: boolean;
  page: any;
  sendFullAppToInterestsEnabled: boolean;
  viewer: Viewer;
  navigate: any;
  weightedPrefsEnabled: boolean;
}

export const useSearchManager = (defaultSorting = "POSITION_NUMBER") => {
  const {
    setTotalTableResults,
    setTableResults,
  } = useInterestsTable();
  const {
    searchState,
    pageState, filterState, filterCondition,
    textFilter,
    searchFlag,
    setSearchState,
    setNbPages,
    setSearchFlag,
    setSelectedPage,
    interestListIds,
    setInterestListIds,
  } = useSidebarFilters();

  const searchParams =
  ():LazyQueryHookOptions<ApplicantSearch, ApplicantSearchVariables> => ({ variables: {
    elasticQuery: {
      filters: formatFilters(filterState),
      filterCondition,
      textFilter,
      defaultSorting,
    },
    interestListIds,
    page: pageState.page,
    pageSize: pageState.pageSize,
  },
  fetchPolicy: "no-cache" });

  const [run, { loading, error, data }] =
  useLazyQuery<ApplicantSearch, ApplicantSearchVariables>(APPLICANT_SEARCH_QUERY, searchParams());

  const search = () => {
    run(searchParams());
  };

  const [starMap, setStarMap] = useState<StarMapType>({});
  const onResult =
  (event: {nbHits: number, hits: TableResult[], page?: number, nbPages?: number}) => {
    const { hits } = event;
    if (hits.length > 0) {
      const newStarMap = {} as StarMapType;
      hits.forEach((hit) => {
        newStarMap[hit.id] = hit.hasStar;
      });
      setStarMap(newStarMap);
    }

    setTotalTableResults(event.nbHits);
    setTableResults(event.hits);
  };

  useEffect(() => {
    onResult(searchState);
  }, [searchState.hits]);


  useEffect(() => {
    search();
    setSearchFlag(false);
  }, [
    pageState.page, pageState.nbPages, filterState, filterCondition,
    textFilter, searchFlag, interestListIds,
  ]);

  useEffect(() => {
    if (data) {
      const newNbPages = Math.ceil(data.applicantSearch.hits / data.applicantSearch.pageSize);
      setNbPages(newNbPages);
      setSearchState({
        page: data.applicantSearch.page,
        nbHits: data.applicantSearch.hits,
        hits: data.applicantSearch.items,
      });
      if (newNbPages < pageState.page && newNbPages > 0) {
        setSelectedPage(newNbPages - 1);
      }
    }
  }, [loading, error, data]);

  const onStar = (id: string) => {
    setStarMap((oldStarMap) => ({
      ...oldStarMap,
      [id]: !oldStarMap[id],
    }));
  };

  const page = pageState.page || 0;
  const basePosition = page * pageState.pageSize;

  return {
    onStar,
    starMap,
    setStarMap,
    basePosition,
    search,
    loading,
    setSelectedPage,
    interestListIds,
    setInterestListIds,
  };
};

const TableContainer: React.FC<Props> = (props) => {
  const {
    viewer,
    slideoutEnabled,
    interestListId,
    sendFullAppToInterestsEnabled,
    defaultSorting,
    weightedPrefsEnabled,
  } = props;
  const {
    tableResults,
    selectedInterestIds,
  } = useInterestsTable();

  const {
    onStar,
    starMap,
    basePosition,
    search,
    interestListIds,
    setInterestListIds,
  } = useSearchManager(defaultSorting);

  useEffect(() => {
    setInterestListIds(interestListId === "all" && viewer.lists ? viewer.lists?.map(({ id }) => id) : [interestListId]);
    search();
  }, []);

  const query = useQuery<getViewer>(VIEWER_QUERY);

  const interestListObj = query.data?.viewer?.lists?.reduce((acc, list) => {
    acc[list.id] = list.name;
    return acc;
  }, {} as Record<string, string>);

  // M3-T02 — prefer the freshly queried viewer (it selects
  // organization.whoCanAssign) over the page-level prop.
  const canAssignOwner = useCanAssignOwner(query.data?.viewer ?? viewer);

  return (
    <div style={{ width: "100%" }}>
      <div className="interest-table" style={{ overflowX: "auto", overflowY: "hidden" }}>
        <SelectionPrompt
          totalSelectionCount={tableResults.length}
          selectedCount={selectedInterestIds.length}
          viewer={viewer}
          currentListId={interestListId}
        />
        <table>
          <colgroup>
            <col span={1} style={{ width: "24px" }} />
            <col span={1} style={{ width: "24px" }} />
            <col span={1} style={{ width: "48px" }} />
            <col span={1} style={{ width: "200px" }} />
            {interestListIds.length > 1 && <col span={1} style={{ width: "200px" }} />}
            <col span={1} style={{ width: "74px" }} />
            <col span={1} style={{ width: "114px" }} />
            <col span={1} style={{ width: "96px" }} />
            <col span={1} style={{ width: "96px" }} />
            <col span={1} style={{ width: "142px" }} />
            <col span={1} style={{ width: "160px" }} />
            <col span={1} style={{ width: "200px" }} />
          </colgroup>
          <thead>
            <tr>
              <th />
              <th />
              <th />
              <th>Name</th>
              {interestListIds.length > 1 && <th>List Name</th>}
              <th>Status</th>
              <th>Phone Number</th>
              <th>Family Size</th>
              <th>Unit</th>
              <th>Income</th>
              <th>Owner</th>
              <th style={{ textAlign: "right" }}>Received</th>
            </tr>
          </thead>
          {!!tableResults.length && (
            <tbody>
              {tableResults.map((hit, i) => (
                <Row
                  key={`${hit.id}.${hit.status}`}
                  offset={basePosition + i}
                  interest={hit}
                  onStar={() => onStar(hit.id)}
                  hasStar={starMap[hit.id]}
                  slideoutEnabled={slideoutEnabled}
                  interestListObj={interestListIds.length > 1 ? (interestListObj ?? null) : null}
                  weightedPrefsEnabled={weightedPrefsEnabled}
                  currentUserId={viewer.id}
                  currentUserName={viewer.fullName ?? ""}
                  activeInterestListId={interestListId}
                  canAssignOwner={canAssignOwner}
                />
              ))}
            </tbody>
          )}
        </table>
        <Pagination
          interestListId={interestListId}
          requestedPage={props.page}
          navigate={props.navigate}
        />
        {slideoutEnabled && (
          <Routes>
            <Route path="interests/:interestId/*" element={<InterestProfileSlideout
              viewer={viewer}
              onStar={onStar}
              listId={interestListId}
              sendFullAppToInterestsEnabled={sendFullAppToInterestsEnabled}
              onClose={search}
            />} />
          </Routes>
        )}
      </div>
    </div>
  );
};

export default TableContainer;
